import DOMPurify from "dompurify";

/**
 * Sanitizes HTML content and adds sandbox attributes to iframes for security.
 * This prevents XSS attacks and ensures iframes are sandboxed.
 *
 * @param html - The HTML string to sanitize
 * @returns Sanitized HTML with secured iframes
 */
export const sanitizeHtml = (html: string): string => {
  // Configure DOMPurify to allow iframes but sanitize their content
  const config = {
    ALLOWED_TAGS: [
      "p",
      "br",
      "strong",
      "em",
      "del",
      "code",
      "pre",
      "h1",
      "h2",
      "h3",
      "h4",
      "h5",
      "h6",
      "ul",
      "ol",
      "li",
      "blockquote",
      "hr",
      "a",
      "img",
      "iframe",
    ],
    ALLOWED_ATTR: [
      "href",
      "target",
      "rel",
      "src",
      "alt",
      "class",
      "width",
      "height",
      "frameborder",
      "allow",
      "sandbox",
      "loading",
      "title",
    ],
    KEEP_CONTENT: true,
  };

  // First pass: sanitize HTML
  let sanitized = DOMPurify.sanitize(html, config);

  // Second pass: add sandbox attribute to iframes
  // This creates a temporary container to manipulate the HTML
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = sanitized;

  // Find all iframes and add/update sandbox attribute
  const iframes = tempDiv.querySelectorAll("iframe");
  iframes.forEach((iframe) => {
    // Add comprehensive sandbox restrictions
    const existingSandbox = iframe.getAttribute("sandbox");
    const sandboxValue = existingSandbox
      ? `${existingSandbox} allow-scripts allow-same-origin`
      : "allow-scripts allow-same-origin allow-popups";

    iframe.setAttribute("sandbox", sandboxValue);
    iframe.setAttribute("loading", "lazy");
  });

  // Return the modified HTML
  return tempDiv.innerHTML;
};
